import { useRef, useCallback, useState, useEffect } from 'react';

interface UsePinchZoomOptions {
  /** Whether gesture handling is active. Must be true when the container is in the DOM. */
  enabled?: boolean;
  /** Minimum scale factor */
  minScale?: number;
  /** Maximum scale factor */
  maxScale?: number;
  /** Scale applied on double-tap */
  doubleTapScale?: number;
  /** Max time in ms between taps to count as double-tap */
  doubleTapDelay?: number;
}

interface UsePinchZoomReturn {
  /** Callback ref — attach to the container element */
  setContainerRef: (el: HTMLDivElement | null) => void;
  scale: number;
  translateX: number;
  translateY: number;
  isZoomed: boolean;
  resetZoom: () => void;
  /** True during the 200ms double-tap zoom animation */
  animating: boolean;
}

/** Distance between two touch points */
function getTouchDistance(t1: Touch, t2: Touch): number {
  const dx = t1.clientX - t2.clientX;
  const dy = t1.clientY - t2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

/** Midpoint between two touch points */
function getTouchMidpoint(t1: Touch, t2: Touch): { x: number; y: number } {
  return {
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2,
  };
}

/**
 * Clamp translate values so the image edge can't be dragged past the viewport center.
 * At scale > 1, the image is larger than the container — allow panning up to
 * half the overflow in each direction.
 */
function clampTranslate(
  tx: number,
  ty: number,
  scale: number,
  containerWidth: number,
  containerHeight: number,
): { tx: number; ty: number } {
  if (scale <= 1) return { tx: 0, ty: 0 };

  const maxTx = ((scale - 1) * containerWidth) / 2;
  const maxTy = ((scale - 1) * containerHeight) / 2;

  return {
    tx: Math.max(-maxTx, Math.min(maxTx, tx)),
    ty: Math.max(-maxTy, Math.min(maxTy, ty)),
  };
}

/**
 * Hook for pinch-to-zoom, pan, and double-tap zoom on touch devices.
 *
 * Uses Touch Events API (not Pointer Events) because `e.touches` provides
 * all active touch points in a single event, which is natural for multi-finger gestures.
 *
 * Returns a callback ref (`setContainerRef`) instead of a plain ref object.
 * This attaches touch listeners immediately when the element mounts — critical
 * for react-modal portals where useEffect timing can miss the mount.
 */
export const usePinchZoom = ({
  enabled = true,
  minScale = 1,
  maxScale = 3,
  doubleTapScale = 2,
  doubleTapDelay = 300,
}: UsePinchZoomOptions = {}): UsePinchZoomReturn => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [animating, setAnimating] = useState(false);

  // Refs for gesture tracking (updated every frame, no re-renders needed)
  const pinchStartDistance = useRef<number | null>(null);
  const pinchStartScale = useRef(1);
  const pinchOrigin = useRef<{ x: number; y: number } | null>(null);

  const panStartPoint = useRef<{ x: number; y: number } | null>(null);
  const panStartTranslate = useRef<{ tx: number; ty: number }>({ tx: 0, ty: 0 });

  const lastTapTime = useRef(0);
  const lastTapPoint = useRef<{ x: number; y: number } | null>(null);
  const containerRect = useRef<DOMRect | null>(null);
  const animationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep current values in refs so event handlers always see latest state
  const scaleRef = useRef(scale);
  const txRef = useRef(translateX);
  const tyRef = useRef(translateY);
  const minScaleRef = useRef(minScale);
  const maxScaleRef = useRef(maxScale);
  const doubleTapScaleRef = useRef(doubleTapScale);
  const doubleTapDelayRef = useRef(doubleTapDelay);
  scaleRef.current = scale;
  txRef.current = translateX;
  tyRef.current = translateY;
  minScaleRef.current = minScale;
  maxScaleRef.current = maxScale;
  doubleTapScaleRef.current = doubleTapScale;
  doubleTapDelayRef.current = doubleTapDelay;

  const resetZoom = useCallback(() => {
    if (animationTimer.current !== null) {
      clearTimeout(animationTimer.current);
      animationTimer.current = null;
    }
    setAnimating(false);
    setScale(minScaleRef.current);
    setTranslateX(0);
    setTranslateY(0);
    pinchStartDistance.current = null;
    panStartPoint.current = null;
    lastTapTime.current = 0;
    lastTapPoint.current = null;
    containerRect.current = null;
  }, []);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      const container = containerRef.current;
      if (!container) return;

      if (e.touches.length === 2) {
        // Pinch start
        e.preventDefault();
        const dist = getTouchDistance(e.touches[0], e.touches[1]);
        pinchStartDistance.current = dist;
        pinchStartScale.current = scaleRef.current;
        pinchOrigin.current = getTouchMidpoint(e.touches[0], e.touches[1]);
        containerRect.current = container.getBoundingClientRect();
        // Capture current translate so the pinch offset builds on top of it
        panStartTranslate.current = { tx: txRef.current, ty: tyRef.current };
        // Cancel any single-finger pan in progress
        panStartPoint.current = null;
      } else if (e.touches.length === 1) {
        const now = Date.now();
        const touch = e.touches[0];
        const tapPoint = { x: touch.clientX, y: touch.clientY };

        // Check for double-tap
        if (
          lastTapPoint.current &&
          now - lastTapTime.current < doubleTapDelayRef.current &&
          Math.abs(tapPoint.x - lastTapPoint.current.x) < 30 &&
          Math.abs(tapPoint.y - lastTapPoint.current.y) < 30
        ) {
          e.preventDefault();
          const targetScale = doubleTapScaleRef.current;
          if (animationTimer.current !== null) {
            clearTimeout(animationTimer.current);
          }
          setAnimating(true);
          animationTimer.current = setTimeout(() => {
            setAnimating(false);
            animationTimer.current = null;
          }, 200);
          // Toggle between 1x and doubleTapScale
          if (scaleRef.current > minScaleRef.current) {
            setScale(minScaleRef.current);
            setTranslateX(0);
            setTranslateY(0);
            pinchStartDistance.current = null;
            panStartPoint.current = null;
            containerRect.current = null;
          } else {
            setScale(targetScale);
            // Center zoom on tap point
            const rect = container.getBoundingClientRect();
            containerRect.current = rect;
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            const offsetX = (centerX - tapPoint.x) * (targetScale - 1);
            const offsetY = (centerY - tapPoint.y) * (targetScale - 1);
            const clamped = clampTranslate(offsetX, offsetY, targetScale, rect.width, rect.height);
            setTranslateX(clamped.tx);
            setTranslateY(clamped.ty);
          }
          lastTapTime.current = 0;
          lastTapPoint.current = null;
          return;
        }

        lastTapTime.current = now;
        lastTapPoint.current = tapPoint;

        // Start pan only when zoomed
        if (scaleRef.current > minScaleRef.current) {
          e.preventDefault();
          panStartPoint.current = { x: touch.clientX, y: touch.clientY };
          panStartTranslate.current = { tx: txRef.current, ty: tyRef.current };
          containerRect.current = container.getBoundingClientRect();
        }
      }
    },
    [resetZoom],
  );

  const handleTouchMove = useCallback((e: TouchEvent) => {
    const container = containerRef.current;
    if (!container) return;

    if (e.touches.length === 2 && pinchStartDistance.current !== null) {
      // Pinch zoom
      e.preventDefault();
      const dist = getTouchDistance(e.touches[0], e.touches[1]);
      const ratio = dist / pinchStartDistance.current;
      const newScale = Math.max(minScaleRef.current, Math.min(maxScaleRef.current, pinchStartScale.current * ratio));
      setScale(newScale);

      // Translate so zoom feels anchored to the pinch midpoint, preserving any prior pan
      if (pinchOrigin.current) {
        const rect = containerRect.current ?? container.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const scaleDelta = newScale - pinchStartScale.current;
        const offsetX = panStartTranslate.current.tx + (centerX - pinchOrigin.current.x) * scaleDelta;
        const offsetY = panStartTranslate.current.ty + (centerY - pinchOrigin.current.y) * scaleDelta;
        const clamped = clampTranslate(offsetX, offsetY, newScale, rect.width, rect.height);
        setTranslateX(clamped.tx);
        setTranslateY(clamped.ty);
      }
    } else if (e.touches.length === 1 && panStartPoint.current && scaleRef.current > minScaleRef.current) {
      // Single-finger pan when zoomed
      e.preventDefault();
      const touch = e.touches[0];
      const dx = touch.clientX - panStartPoint.current.x;
      const dy = touch.clientY - panStartPoint.current.y;
      const rect = containerRect.current ?? container.getBoundingClientRect();
      const clamped = clampTranslate(
        panStartTranslate.current.tx + dx,
        panStartTranslate.current.ty + dy,
        scaleRef.current,
        rect.width,
        rect.height,
      );
      setTranslateX(clamped.tx);
      setTranslateY(clamped.ty);
    }
  }, []);

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (e.touches.length < 2) {
        const wasPinching = pinchStartDistance.current !== null;
        pinchStartDistance.current = null;
        pinchOrigin.current = null;

        // If scale went below minScale during pinch, snap back
        if (wasPinching && scaleRef.current <= minScaleRef.current) {
          resetZoom();
        }
      }

      if (e.touches.length === 0) {
        panStartPoint.current = null;
        containerRect.current = null;
      }
    },
    [resetZoom],
  );

  const handleTouchCancel = useCallback(() => {
    pinchStartDistance.current = null;
    pinchOrigin.current = null;
    panStartPoint.current = null;
    containerRect.current = null;
    lastTapTime.current = 0;
    lastTapPoint.current = null;
  }, []);

  // Store handler refs so the callback ref always has the latest handlers
  // without needing to re-create the callback (which would detach/reattach on every render)
  const handlersRef = useRef({ handleTouchStart, handleTouchMove, handleTouchEnd, handleTouchCancel });
  handlersRef.current = { handleTouchStart, handleTouchMove, handleTouchEnd, handleTouchCancel };

  // Track the currently-attached element and the exact handler references used,
  // so detach always removes the same functions that were added.
  const attachedRef = useRef<HTMLDivElement | null>(null);
  const attachedHandlers = useRef<typeof handlersRef.current | null>(null);

  const detachListeners = useCallback(() => {
    const el = attachedRef.current;
    const handlers = attachedHandlers.current;
    if (!el || !handlers) return;
    el.removeEventListener('touchstart', handlers.handleTouchStart);
    el.removeEventListener('touchmove', handlers.handleTouchMove);
    el.removeEventListener('touchend', handlers.handleTouchEnd);
    el.removeEventListener('touchcancel', handlers.handleTouchCancel);
    attachedRef.current = null;
    attachedHandlers.current = null;
  }, []);

  const attachListeners = useCallback(
    (el: HTMLDivElement) => {
      detachListeners();
      const handlers = handlersRef.current;
      attachedHandlers.current = handlers;
      // { passive: false } so we can call preventDefault to stop browser scroll/zoom
      el.addEventListener('touchstart', handlers.handleTouchStart, { passive: false });
      el.addEventListener('touchmove', handlers.handleTouchMove, { passive: false });
      el.addEventListener('touchend', handlers.handleTouchEnd);
      el.addEventListener('touchcancel', handlers.handleTouchCancel);
      attachedRef.current = el;
    },
    [detachListeners],
  );

  // Callback ref: attaches listeners the moment the DOM element mounts.
  // When React calls this with null (unmount) or a new element, we clean up the old one.
  const setContainerRef = useCallback(
    (el: HTMLDivElement | null) => {
      detachListeners();
      containerRef.current = el;

      if (el && enabled) {
        attachListeners(el);
      }
    },
    [enabled, attachListeners, detachListeners],
  );

  // Clean up animation timer on unmount
  useEffect(() => {
    return () => {
      if (animationTimer.current !== null) {
        clearTimeout(animationTimer.current);
      }
    };
  }, []);

  return {
    setContainerRef,
    scale,
    translateX,
    translateY,
    isZoomed: scale > minScale,
    resetZoom,
    animating,
  };
};
